/**
 * Opaque, DEVICE-LOCAL aliases for accounts — the payload of the `?as=`
 * handoff that carries the active account into a NEW tab.
 */

import { generateSecretToken } from "@fleet/utils";

const ALIAS_STORAGE_KEY = "account_aliases";

/** alias → userId. Device-local; never leaves this browser profile. */
type AliasMap = Record<string, string>;

function readAliasMap(): AliasMap {
  try {
    const raw = globalThis.localStorage.getItem(ALIAS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const map: AliasMap = {};
    for (const [alias, userId] of Object.entries(parsed)) {
      if (
        typeof alias === "string" &&
        typeof userId === "string" &&
        alias !== "" &&
        userId !== ""
      ) {
        map[alias] = userId;
      }
    }
    return map;
  } catch {
    return {};
  }
}

function writeAliasMap(map: AliasMap): void {
  try {
    globalThis.localStorage.setItem(ALIAS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage unavailable
  }
}

/** Mints a random, non-guessable alias. */
function mintAlias(): string {
  return generateSecretToken();
}

/**
 * The alias for `userId`, minting and persisting one on first use. Stable for
 * the life of the account on this device, so repeated links are identical.
 */
export function aliasForAccount(userId: string): string {
  const map = readAliasMap();
  for (const [alias, id] of Object.entries(map)) {
    if (id === userId) {
      return alias;
    }
  }
  const alias = mintAlias();
  map[alias] = userId;
  writeAliasMap(map);
  return alias;
}

/**
 * Resolves a `?as=` alias back to a userId on THIS device, or `null` when the
 * alias is unknown.
 */
export function accountIdForAlias(alias: string): string | null {
  return readAliasMap()[alias] ?? null;
}

/**
 * Drops aliases whose account no longer exists.
 */
export function pruneAliases(knownUserIds: readonly string[]): void {
  const map = readAliasMap();
  const known = new Set(knownUserIds);
  let changed = false;
  for (const [alias, userId] of Object.entries(map)) {
    if (!known.has(userId)) {
      delete map[alias];
      changed = true;
    }
  }
  if (changed) {
    writeAliasMap(map);
  }
}
